/** Convert a string to kebab-case (e.g. "Guest Passes" → "guest-passes"). */
export function toKebab(s: string): string {
  return s.toLowerCase().replace(/\s+/g, "-");
}

/** Convert a string to snake_case (e.g. "Membership sections" → "membership_sections"). */
export function toSnake(s: string): string {
  return s.toLowerCase().replace(/\s+/g, "_");
}

/** Extract 4-digit year from a date string (e.g. "5/5/2002" → "2002"). */
export function extractYear(dateStr: string): string {
  if (!dateStr) return '';
  const match = dateStr.match(/(\d{4})/);
  if (match) return match[1];
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? '' : String(date.getFullYear());
}

/** Format a date string to "Month D, YYYY" (e.g. "5/5/2026" → "May 5, 2026"). */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  // Parse YYYY-MM-DD strings as local time to avoid UTC-offset shift.
  // new Date('YYYY-MM-DD') parses as UTC midnight, which shifts the date back one day
  // for users in negative-offset timezones (e.g. US Eastern).
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}/.test(dateStr);
  let date: Date;
  if (isoDateOnly) {
    const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateStr);
  }
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** Format a time object to "H:MM AM/PM" (e.g. { hour: 10, minute: 30 } → "10:30 AM"). */
export function formatTime({ hour, minute }: { hour: number; minute: number }): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const m = String(minute).padStart(2, '0');
  return `${h}:${m} ${period}`;
}

/** Strip HTML tags from a string, collapsing whitespace to a single space. */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.replace(/\s+/g, ' ').trim() ?? '';
}

/**
 * Converts an HTML string to newline-separated plain text (server-safe, no DOM).
 * Block-level closing tags (`</li>`, `</p>`, `</div>`) and `<br>` become newlines.
 * Common HTML entities are decoded. Runs of 3+ newlines are collapsed to two.
 */
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<\/(li|p|div)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Validate email format. Returns an error message or null if valid. */
export function validateEmail(email: string): string | null {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? null : 'Invalid email format';
}

/**
 * Format an ISO date string to abbreviated month format used in booking displays
 * (e.g. "2025-08-07T..." → "Aug. 7, 2025").
 * Parses the date portion only to avoid timezone shifts.
 */
export function formatBookingDate(dateStr: string): string {
  const datePart = dateStr.substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const monthStr = d.toLocaleDateString('en-US', { month: 'short' });
  return `${monthStr}. ${day}, ${year}`;
}

/**
 * Format a start/end time pair to a display string (e.g. "9:30 AM" or "6:00 PM - 8:00 PM").
 * Uses a hyphen separator consistent with visit display design.
 */
export function formatTimeRange(
  start: { hour: number; minute: number },
  end?: { hour: number; minute: number } | null
): string {
  const startStr = formatTime(start);
  if (!end) return startStr;
  return `${startStr} - ${formatTime(end)}`;
}

/** Returns true if the given date string is within 30 days from today. */
export function isExpiringSoon(dateStr: string): boolean {
  if (!dateStr) return false;
  const daysLeft = (new Date(dateStr).getTime() - Date.now()) / 86400000;
  return daysLeft > 0 && daysLeft <= 30;
}

/** Format an ISO date string to M/D/YYYY (e.g. "2025-07-20T..." → "7/20/2025"). */
export function formatShortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const parts = iso.substring(0, 10).split("-").map(Number);
  if (parts.length < 3) return null;
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

/** Returns true if the given ISO date expired within the last 2 months (~60 days). */
export function isRecentlyExpired(dateStr?: string): boolean {
  if (!dateStr) return false;
  const expiry = new Date(dateStr).getTime();
  const now = Date.now();
  const twoMonthsMs = 60 * 24 * 60 * 60 * 1000;
  return expiry < now && now - expiry <= twoMonthsMs;
}

export function isValidUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  return value.startsWith("http://") || value.startsWith("https://");
}

/**
 * Format a time or time range in compact form for the Booked Visits card.
 * Single time: "9:00AM". Range: "10:30 – 11:30AM" (period omitted from start
 * when both times share the same AM/PM; shown on both when they differ).
 */
export function formatVisitTime(
  start: { hour: number; minute: number },
  end?: { hour: number | null; minute: number | null } | null,
): string {
  const startPeriod = start.hour >= 12 ? "PM" : "AM";
  const startH = start.hour % 12 || 12;
  const startM = String(start.minute).padStart(2, "0");

  if (!end || end.hour == null || end.minute == null) {
    return `${startH}:${startM}${startPeriod}`;
  }

  const endPeriod = end.hour >= 12 ? "PM" : "AM";
  const endH = end.hour % 12 || 12;
  const endM = String(end.minute).padStart(2, "0");
  const startStr = startPeriod === endPeriod ? `${startH}:${startM}` : `${startH}:${startM}${startPeriod}`;
  return `${startStr} – ${endH}:${endM}${endPeriod}`;
}
